import React, { useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, FormSpy, useForm } from "react-final-form";
import classNames from "classnames";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import ScrollBar from "components/ScrollBar";
import { DEFAULT_TARGET, FormFields as OptimizationFormFields } from "../../index";
import LearnMoreLink from "rs-components/LearnMoreLink";
import ErrorMessage from "./components/ErrorMessage";
import OutputDescription from "./components/OutputDescription";
import { HELP_SYSTEM_URL, HelpId } from "shared/helpSystemBindings";
import { isNotEmpty } from "utils/validate";
import { processVariablesSelector } from "store/global/global.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { completedWorkspaceInsightsOptionsSelector, persistedInsightsDataSelector, incompleteSimulationsIdsSelector, workspaceCellModelsSelector, workspaceCurrentOptimizationIdSelector, workspaceInsightsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, OUTPUT_FINAL_ID, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { CancelOutdatedPreviewType, OptimizationTargetType, } from "store/rs-workspace/rs-workspace.types";
import { clearPreviewRequest, resetOptimizationPreview, } from "store/rs-workspace/actions/rs-workspace.actions";
import { getInsightInputSetOptions, getInsightOutputOptions } from "store/rs-workspace/utils/insights.utils";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getOptimizationInputsNames, prepareParametersOnChangeOutput, } from "store/rs-workspace/utils/scenario.utils";
import { isInsightCompleted, isInsightInputSetCompleted } from "store/rs-workspace/utils/completeness.utils";
import { EDIT_RS_WORKSPACE_MESSAGES } from "shared/labels";
import * as styles from "./styles.module.scss";
export var FormFields;
(function (FormFields) {
    FormFields["insightId"] = "insightId";
    FormFields["insightInputSetId"] = "insightInputSetId";
    FormFields["outputId"] = "outputId";
    FormFields["combined"] = "combined";
    FormFields["operation"] = "operation";
    FormFields["optimizationTarget"] = "optimizationTarget";
})(FormFields || (FormFields = {}));
const InputsToOptimize = ({ restrictions, currentOptimization }) => {
    const dispatch = useDispatch();
    const parentForm = useForm();
    const editable = useSelector(isEditableSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const workspaceSimulations = useSelector(workspaceSimulationsSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const workspaceCellModels = useSelector(workspaceCellModelsSelector);
    const workspaceProcessData = useSelector(workspaceProcessDataSelector);
    const processVariables = useSelector(processVariablesSelector);
    const currentOptimizationId = useSelector(workspaceCurrentOptimizationIdSelector);
    const workspaceInsights = useSelector(workspaceInsightsSelector);
    const incompleteSimulationsIds = useSelector(incompleteSimulationsIdsSelector);
    const insightOptions = useSelector(completedWorkspaceInsightsOptionsSelector(parentForm.getState()?.values?.state?.input?.insightId));
    const targetOptions = useMemo(() => {
        return Object.values(OptimizationTargetType).map((value) => value === OptimizationTargetType.CELL_MODEL ? { label: "Cell Model", value } : { label: value, value });
    }, []);
    const resetPreview = useCallback(() => {
        dispatch(resetOptimizationPreview(currentOptimizationId ?? -1));
        dispatch(clearPreviewRequest({
            previewType: CancelOutdatedPreviewType.Optimization,
            elementId: currentOptimizationId ?? -1,
        }));
    }, [currentOptimizationId, dispatch]);
    const handleChangeInsightId = useCallback((form) => (value) => {
        const prevValue = form.getFieldState(FormFields.insightId)?.value;
        if (prevValue !== value) {
            form.reset({ [FormFields.insightId]: value });
            parentForm.change(OptimizationFormFields.state_parameters);
            resetPreview();
        }
    }, [parentForm, resetPreview]);
    const handleChangeInsightInputSetId = useCallback((form) => (value) => {
        const prevValue = form.getFieldState(FormFields.insightInputSetId)?.value;
        if (prevValue !== value) {
            const insightId = form.getFieldState(FormFields.insightId)?.value;
            const outputId = getInsightOutputOptions(insightId, value, currentOptimization?.state.input?.outputId, workspaceInsights, persistedInsightsData, processVariables, workspaceRecipes)[0]?.value ?? undefined;
            form.reset({
                [FormFields.insightId]: insightId,
                [FormFields.insightInputSetId]: value,
                [FormFields.outputId]: outputId,
                [FormFields.combined]: outputId === OUTPUT_FINAL_ID,
                [FormFields.optimizationTarget]: form.getFieldState(FormFields.optimizationTarget)?.value ?? DEFAULT_TARGET,
            });
            parentForm.change(OptimizationFormFields.state_parameters, prepareParametersOnChangeOutput(insightId, value, outputId, parentForm.getFieldState(OptimizationFormFields.state_parameters)?.value ?? [], workspaceInsights, workspaceSimulations));
            resetPreview();
        }
    }, [
        currentOptimization?.state.input?.outputId,
        workspaceInsights,
        persistedInsightsData,
        processVariables,
        workspaceRecipes,
        parentForm,
        workspaceSimulations,
        resetPreview,
    ]);
    const handleChangeOutputId = useCallback((form) => (value) => {
        const prevValue = form.getFieldState(FormFields.outputId)?.value;
        if (prevValue !== value) {
            form.batch(() => {
                form.change(FormFields.combined, value === OUTPUT_FINAL_ID);
                form.change(FormFields.outputId, value);
            });
            const insightId = form.getFieldState(FormFields.insightId)?.value;
            const insightInputSetId = form.getFieldState(FormFields.insightInputSetId)?.value;
            const parameters = parentForm.getFieldState(OptimizationFormFields.state_parameters)?.value ?? [];
            parentForm.change(OptimizationFormFields.state_parameters, prepareParametersOnChangeOutput(insightId, insightInputSetId, value, parameters, workspaceInsights, workspaceSimulations));
            resetPreview();
        }
    }, [parentForm, resetPreview, workspaceInsights, workspaceSimulations]);
    const handleChangeTarget = useCallback((form) => (value) => {
        const prevValue = form.getFieldState(FormFields.optimizationTarget)?.value;
        if (prevValue !== value) {
            form.change(FormFields.optimizationTarget, value);
            const insightId = form.getFieldState(FormFields.insightId)?.value;
            const insightInputSetId = form.getFieldState(FormFields.insightInputSetId)?.value;
            const outputId = form.getFieldState(FormFields.outputId)?.value;
            parentForm.change(OptimizationFormFields.state_parameters, prepareParametersOnChangeOutput(insightId, insightInputSetId, outputId, [], workspaceInsights, workspaceSimulations));
            resetPreview();
        }
    }, [parentForm, resetPreview, workspaceInsights, workspaceSimulations]);
    const renderInputNamesList = useCallback((form) => {
        const insightId = form.getFieldState(FormFields.insightId)?.value;
        const selectedInsight = workspaceInsights.find(({ id }) => id === insightId);
        return getOptimizationInputsNames(workspaceSimulations, workspaceProcessData, selectedInsight, form.getFieldState(FormFields.insightInputSetId)?.value, form.getFieldState(FormFields.outputId)?.value).map(({ name, key }) => (React.createElement("p", { key: key, className: styles["insightInputSet-input"] }, name)));
    }, [workspaceInsights, workspaceProcessData, workspaceSimulations]);
    const handleChange = useCallback(({ values }) => {
        // setTimeout used to allow the form to finish rendering and setting up its side effects during first render
        setTimeout(() => parentForm.change(OptimizationFormFields.state_input, {
            ...values,
            insightId: values?.[FormFields.insightId],
            insightInputSetId: values?.[FormFields.insightInputSetId],
            outputId: values?.[FormFields.outputId],
        }), 0);
    }, [parentForm]);
    const getInsightInputSetExternalError = useCallback((insightId, inputSetId) => {
        const currentInsight = workspaceInsights.find(({ id }) => id === insightId);
        const currentInputSetId = currentInsight?.state.input.inputSets.find(({ id }) => id === inputSetId);
        const isComplete = currentInputSetId &&
            currentInsight?.state.outputs &&
            isInsightInputSetCompleted(currentInputSetId, currentInsight?.state.outputs, processVariables, incompleteSimulationsIds, workspaceSimulations, workspaceCellModels, workspaceRecipes);
        if (insightId && inputSetId && !isComplete) {
            return EDIT_RS_WORKSPACE_MESSAGES.INPUT_SET_IS_INCOMPLETE;
        }
        return undefined;
    }, [
        workspaceInsights,
        processVariables,
        incompleteSimulationsIds,
        workspaceSimulations,
        workspaceCellModels,
        workspaceRecipes,
    ]);
    const getInsightExternalError = useCallback((insightId) => {
        const currentInsight = workspaceInsights.find(({ id }) => id === insightId);
        if (!currentInsight || !currentOptimization?.state.input?.insightInputSetId) {
            return undefined;
        }
        const isInsightComplete = isInsightCompleted(currentInsight?.state, processVariables, incompleteSimulationsIds, workspaceSimulations, workspaceCellModels, workspaceRecipes);
        if (!isInsightComplete) {
            return EDIT_RS_WORKSPACE_MESSAGES.INSIGHT_IS_INCOMPLETE;
        }
        return undefined;
    }, [
        currentOptimization?.state.input?.insightInputSetId,
        incompleteSimulationsIds,
        workspaceInsights,
        processVariables,
        workspaceSimulations,
        workspaceCellModels,
        workspaceRecipes,
    ]);
    return (React.createElement(Form, { onSubmit: (values) => values, initialValues: parentForm.getState()?.initialValues?.state?.input }, ({ values, form }) => (React.createElement(React.Fragment, null,
        editable && React.createElement(FormSpy, { onChange: handleChange, subscription: { values: true } }),
        React.createElement("div", { className: styles.scrollBarWrapper },
            React.createElement(ScrollBar, null,
                React.createElement("div", { className: styles.root },
                    React.createElement("h3", { className: styles.title }, "Objective"),
                    React.createElement(ErrorMessage, null),
                    React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, label: "Insight to optimize", placeholder: "Select", name: FormFields.insightId, editable: editable, items: insightOptions, autoFocus: true, restrictions: getFieldRestrictions(restrictions, [
                            getRequestName(`${OptimizationFormFields.state_input}.${FormFields.insightId}`),
                        ]), classes: { container: styles.field, component: styles.selectWrapper }, valueFormatter: ({ value }) => value, externalError: getInsightExternalError(values?.[FormFields.insightId]), disableFormChange: true, onChange: handleChangeInsightId(form), readonly: !editable }),
                    isNotEmpty(values?.[FormFields.insightId]) && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: classNames(styles.field, styles.insightInputSet) },
                            React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, label: "Use data from", placeholder: "Select", name: FormFields.insightInputSetId, editable: editable, items: getInsightInputSetOptions(values?.[FormFields.insightId], currentOptimization?.state.input?.insightInputSetId, workspaceInsights, processVariables, incompleteSimulationsIds, workspaceSimulations, workspaceCellModels, workspaceRecipes), restrictions: getFieldRestrictions(restrictions, [
                                    getRequestName(`${OptimizationFormFields.state_input}.${FormFields.insightInputSetId}`),
                                ]), classes: { component: styles.selectWrapper }, valueFormatter: ({ value }) => value, externalError: getInsightInputSetExternalError(values?.[FormFields.insightId], values?.[FormFields.insightInputSetId]), disableFormChange: true, onChange: handleChangeInsightInputSetId(form), readonly: !editable }),
                            React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, label: "Insight output", placeholder: "Select", name: FormFields.outputId, editable: editable, disabled: !isNotEmpty(values?.[FormFields.insightInputSetId]), items: getInsightOutputOptions(values?.[FormFields.insightId], values?.[FormFields.insightInputSetId], currentOptimization?.state.input?.outputId, workspaceInsights, persistedInsightsData, processVariables, workspaceRecipes), valueFormatter: ({ value }) => value, disableFormChange: true, classes: {
                                    container: classNames(styles.field, styles.insightOutput),
                                    component: styles.selectWrapper,
                                }, onChange: handleChangeOutputId(form), readonly: !editable }),
                            renderInputNamesList(form)),
                        React.createElement(OutputDescription, { objective: values }),
                        React.createElement(Input, { component: RadioInputGroup, name: FormFields.optimizationTarget, label: "Target", items: targetOptions, restrictions: {
                                Default: DEFAULT_TARGET,
                                ...getFieldRestrictions(restrictions, [
                                    getRequestName(`${OptimizationFormFields.state_input}.${FormFields.optimizationTarget}`),
                                ]),
                            }, classes: { container: classNames(styles.field, styles.target) }, disableFormChange: true, onChange: handleChangeTarget(form), readonly: !editable }))),
                    React.createElement(LearnMoreLink, { className: styles["learn-more-link"], url: `${HELP_SYSTEM_URL}#cshid=${HelpId.OPTIMIZATIONS}` }))))))));
};
export default InputsToOptimize;
